import React, { useState } from 'react';
import { PlusIcon, EditIcon, TrashIcon, SearchIcon, EyeIcon, LockIcon, UnlockIcon, MessageSquareIcon, ShieldIcon } from 'lucide-react';
import { NoteType, NoteVisibility } from './NotesManagement';
interface NoteTypesManagementProps {
  noteTypes: NoteType[];
  setNoteTypes: React.Dispatch<React.SetStateAction<NoteType[]>>;
  locale: string;
}
export function NoteTypesManagement({
  noteTypes,
  setNoteTypes,
  locale
}: NoteTypesManagementProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingNoteType, setEditingNoteType] = useState<NoteType | null>(null);
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    defaultVisibility: NoteVisibility;
    isEditable: boolean;
    isSystemProtected: boolean;
  }>({
    name: '',
    description: '',
    defaultVisibility: 'Public',
    isEditable: true,
    isSystemProtected: false
  });
  // Apply search filter
  const filteredNoteTypes = noteTypes.filter(noteType => {
    return noteType.name.toLowerCase().includes(searchTerm.toLowerCase()) || noteType.description && noteType.description.toLowerCase().includes(searchTerm.toLowerCase());
  });
  const handleAddNoteType = () => {
    setFormData({
      name: '',
      description: '',
      defaultVisibility: 'Public',
      isEditable: true,
      isSystemProtected: false
    });
    setEditingNoteType(null);
    setShowForm(true);
  };
  const handleEditNoteType = (noteType: NoteType) => {
    setFormData({
      name: noteType.name,
      description: noteType.description,
      defaultVisibility: noteType.defaultVisibility,
      isEditable: noteType.isEditable,
      isSystemProtected: noteType.isSystemProtected
    });
    setEditingNoteType(noteType);
    setShowForm(true);
  };
  const handleDeleteNoteType = (id: string) => {
    // Check if the note type is system-protected
    const noteType = noteTypes.find(nt => nt.id === id);
    if (noteType?.isSystemProtected) {
      alert('System-protected note types cannot be deleted.');
      return;
    }
    setNoteTypes(noteTypes.filter(noteType => noteType.id !== id));
  };
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    });
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      checked
    } = e.target;
    setFormData({
      ...formData,
      [name]: checked
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingNoteType) {
      // Update existing note type
      setNoteTypes(noteTypes.map(noteType => noteType.id === editingNoteType.id ? {
        ...noteType,
        name: formData.name,
        description: formData.description,
        defaultVisibility: formData.defaultVisibility,
        isEditable: formData.isEditable,
        isSystemProtected: formData.isSystemProtected,
        updatedAt: new Date().toISOString()
      } : noteType));
    } else {
      // Create new note type
      const newNoteType: NoteType = {
        id: `notetype-${String(noteTypes.length + 1).padStart(3, '0')}`,
        name: formData.name,
        description: formData.description,
        defaultVisibility: formData.defaultVisibility,
        isEditable: formData.isEditable,
        isSystemProtected: formData.isSystemProtected,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setNoteTypes([...noteTypes, newNoteType]);
    }
    setShowForm(false);
    setEditingNoteType(null);
  };
  const handleCancel = () => {
    setShowForm(false);
    setEditingNoteType(null);
  };
  // Render visibility badge
  const renderVisibilityBadge = (visibility: NoteVisibility) => {
    switch (visibility) {
      case 'Public':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-19087">
            Public
          </span>;
      case 'Role-based':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-19088">
            Role-based
          </span>;
      case 'User-specific':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-19089">
            User-specific
          </span>;
      case 'Creator-only':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-19090">
            Private
          </span>;
      default:
        return null;
    }
  };
  return <div className="space-y-6" data-id="element-19091">
      {showForm ? <div className="bg-white shadow sm:rounded-lg" data-id="element-19092">
          <div className="px-4 py-5 sm:p-6" data-id="element-19093">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-19094">
              {editingNoteType ? 'Edit Note Type' : 'Add Note Type'}
            </h3>
            <form onSubmit={handleSubmit} className="mt-5 space-y-6" data-id="element-19095">
              <div data-id="element-19096">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-19097">
                  Name <span className="text-red-500" data-id="element-19098">*</span>
                </label>
                <input type="text" name="name" id="name" value={formData.name} onChange={handleFormChange} required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-19099" />
              </div>
              <div data-id="element-19100">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-19101">
                  Description
                </label>
                <textarea name="description" id="description" rows={3} value={formData.description} onChange={handleFormChange} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-19102" />
              </div>
              <div data-id="element-19103">
                <label htmlFor="defaultVisibility" className="block text-sm font-medium text-gray-700" data-id="element-19104">
                  Default Visibility <span className="text-red-500" data-id="element-19105">*</span>
                </label>
                <select name="defaultVisibility" id="defaultVisibility" value={formData.defaultVisibility} onChange={handleFormChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-19106">
                  <option value="Public" data-id="element-19107">Public</option>
                  <option value="Role-based" data-id="element-19108">Role-based</option>
                  <option value="User-specific" data-id="element-19109">User-specific</option>
                  <option value="Creator-only" data-id="element-19110">Creator-only (Private)</option>
                </select>
              </div>
              <div className="space-y-4" data-id="element-19111">
                <div className="relative flex items-start" data-id="element-19112">
                  <div className="flex items-center h-5" data-id="element-19113">
                    <input id="isEditable" name="isEditable" type="checkbox" checked={formData.isEditable} onChange={handleCheckboxChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-19114" />
                  </div>
                  <div className="ml-3 text-sm" data-id="element-19115">
                    <label htmlFor="isEditable" className="font-medium text-gray-700" data-id="element-19116">
                      Editable
                    </label>
                    <p className="text-gray-500" data-id="element-19117">
                      Allow users to edit notes of this type after creation
                    </p>
                  </div>
                </div>
                <div className="relative flex items-start" data-id="element-19118">
                  <div className="flex items-center h-5" data-id="element-19119">
                    <input id="isSystemProtected" name="isSystemProtected" type="checkbox" checked={formData.isSystemProtected} onChange={handleCheckboxChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-19120" />
                  </div>
                  <div className="ml-3 text-sm" data-id="element-19121">
                    <label htmlFor="isSystemProtected" className="font-medium text-gray-700" data-id="element-19122">
                      System Protected
                    </label>
                    <p className="text-gray-500" data-id="element-19123">
                      Prevent this note type from being deleted or modified by
                      regular users
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-3" data-id="element-19124">
                <button type="button" onClick={handleCancel} className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19125">
                  Cancel
                </button>
                <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19126">
                  {editingNoteType ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div> : <>
          <div className="bg-white shadow-sm rounded-lg p-4" data-id="element-19127">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0" data-id="element-19128">
              <div className="flex-1 max-w-md" data-id="element-19129">
                <div className="relative rounded-md shadow-sm" data-id="element-19130">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-19131">
                    <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-19132" />
                  </div>
                  <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search note types..." data-id="element-19133" />
                </div>
              </div>
              <button onClick={handleAddNoteType} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19134">
                <PlusIcon className="h-4 w-4 mr-2" data-id="element-19135" />
                Add Note Type
              </button>
            </div>
          </div>
          <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-19136">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-19137">
              <thead className="bg-gray-50" data-id="element-19138">
                <tr data-id="element-19139">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-19140">
                    Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-19141">
                    Description
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-19142">
                    Default Visibility
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-19143">
                    Settings
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-19144">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-19145">
                {filteredNoteTypes.length === 0 ? <tr data-id="element-19146">
                    <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-19147">
                      No note types found. Click "Add Note Type" to create one.
                    </td>
                  </tr> : filteredNoteTypes.map(noteType => <tr key={noteType.id} className="hover:bg-gray-50" data-id="element-19148">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-19149">
                        <div className="flex items-center" data-id="element-19150">
                          <MessageSquareIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-19151" />
                          <div className="text-sm font-medium text-gray-900" data-id="element-19152">
                            {noteType.name}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-19153">
                        {noteType.description || <span className="text-gray-400 italic" data-id="element-19154">
                            No description
                          </span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-19155">
                        <div className="flex items-center" data-id="element-19156">
                          <EyeIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-19157" />
                          {renderVisibilityBadge(noteType.defaultVisibility)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-19158">
                        <div className="flex space-x-4" data-id="element-19159">
                          <div className="flex items-center" data-id="element-19160">
                            {noteType.isEditable ? <UnlockIcon className="h-4 w-4 text-green-500 mr-1" data-id="element-19161" /> : <LockIcon className="h-4 w-4 text-red-500 mr-1" data-id="element-19162" />}
                            <span data-id="element-19163">
                              {noteType.isEditable ? 'Editable' : 'Not Editable'}
                            </span>
                          </div>
                          {noteType.isSystemProtected && <div className="flex items-center" data-id="element-19164">
                              <ShieldIcon className="h-4 w-4 text-indigo-500 mr-1" data-id="element-19165" />
                              <span data-id="element-19166">Protected</span>
                            </div>}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-19167">
                        <div className="flex justify-end space-x-2" data-id="element-19168">
                          <button onClick={() => handleEditNoteType(noteType)} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-id="element-19169">
                            <EditIcon className="h-4 w-4" data-id="element-19170" />
                          </button>
                          <button onClick={() => handleDeleteNoteType(noteType.id)} className={`${noteType.isSystemProtected ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:text-red-900'}`} title={noteType.isSystemProtected ? 'System-protected types cannot be deleted' : 'Delete'} disabled={noteType.isSystemProtected} data-id="element-19171">
                            <TrashIcon className="h-4 w-4" data-id="element-19172" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
              </tbody>
            </table>
          </div>
        </>}
    </div>;
}